import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";

export const AthleteEditWarmupRoutine = () => {

  const navigate = useNavigate();
  const { staff } = useStaffAuthContext();
  const { record_id } = useParams(); // Get the :id from the URL

  const [phases, setPhases] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selected, setSelected] = useState({
    phases: {},
    exercises: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [savedData, setSavedData] = useState(null);
  const [dateLabel,setDateLabel] = useState("");

  const routineData = [];

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        setError("");
  
        // Step 2: Fetch Athlete Warmup Routine
        const routineResponse = await fetch(`${API_URL.GET_ATHLETE_WARMUP_ROUTINE_BY_ID}${record_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
  
        if (!routineResponse.ok) {
          throw new Error("Failed to fetch the warmup routine");
        }
   
        const routineData = await routineResponse.json();
        console.log("Warmup Routine Data Record:", routineData);

        setDateLabel(new Date(routineData.date).toLocaleDateString());
      
  
        // Step 3: Fetch Phases and Exercises
        const [phasesResponse, exercisesResponse] = await Promise.all([
          fetch(API_URL.GET_PHASE_ALL_DATA, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          }),
          fetch(API_URL.GET_PHASE_EXERCISE_ALL_DATA, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          }),
        ]);
  
        if (!phasesResponse.ok || !exercisesResponse.ok) {
          throw new Error("Failed to fetch phases or exercises data");
        }
  
        const phasesData = await phasesResponse.json();
        const exercisesData = await exercisesResponse.json();
  
        setPhases(phasesData.phases);
        setExercises(exercisesData.exercises);
  
        // Step 4: Initialize Selected State
        const initialSelected = {
          phases: {},
          exercises: {},
        };
  
        routineData.exercises.forEach((sample) => {
          const matchingExercise = exercisesData.exercises.find(
            (ex) => ex._id === sample.exercise_id && ex.phase_id === sample.phase_id
          );
          if (matchingExercise) {
            initialSelected.exercises[sample.exercise_id] = true;
            initialSelected.phases[sample.phase_id] = true;
          }
        });
  
        setSelected(initialSelected);
      } catch (err) {
        console.error(err);
        setError(err.message || "Error fetching data");
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchAllData();
  }, [record_id, staff.token]);
  

  const handleSave = async () => {
    setError("");
    setSuccess("");

    const selectedExercises = exercises
      .filter((ex) => selected.exercises[ex._id])
      .map((ex) => ({
        exercise_id: ex._id,
        phase_id: ex.phase_id,
        title: ex.title,
      }));

    const data = {
      selectedExercises,
    };

    try {
      const response = await fetch(API_URL.EDIT_ATHLETE_WARMUP_ROUTINE_BY_ID + record_id, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) setError("Failed to save data");

     // setSavedData(data);
      setSuccess("Warmup routine saved successfully!");
    } catch (err) {
      setError(err.message || "Error saving data");
    }
  };

  const togglePhase = (phaseId) => {
    const isSelected = !selected.phases[phaseId];
    const updatedExercises = { ...selected.exercises };

    exercises
      .filter((ex) => ex.phase_id === phaseId)
      .forEach((ex) => {
        updatedExercises[ex._id] = isSelected;
      });

    setSelected((prev) => ({
      phases: { ...prev.phases, [phaseId]: isSelected },
      exercises: updatedExercises,
    }));
  };

  const toggleExercise = (exerciseId, phaseId) => {
    const isSelected = !selected.exercises[exerciseId];
    const exercisesInPhase = exercises.filter((ex) => ex.phase_id === phaseId);
    const allChecked = exercisesInPhase.every(
      (ex) =>
        ex._id === exerciseId ? isSelected : selected.exercises[ex._id]
    );

    setSelected((prev) => ({
      phases: { ...prev.phases, [phaseId]: allChecked },
      exercises: { ...prev.exercises, [exerciseId]: isSelected },
    }));
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16">
        <div className="absolute top-4 right-4">
            <button
              onClick={() => navigate(-1)} // Navigate back
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Go Back
            </button>
          </div>

        {isLoading ? (
          <div className="text-center text-blue-500 font-bold text-lg">
            Loading...
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-100 text-green-500 p-3 rounded mb-4">
                {success}
              </div>
            )}

            <h1 className="text-2xl font-bold mb-4">
             Edit  Warmup Routine
            </h1>

            {dateLabel && (
              <div className="bg-white p-4 rounded shadow mb-4">
                <label className="font-bold">Date:</label>
                <span>{dateLabel}</span>
              </div>
            )}

            <div className="bg-white p-4 rounded shadow">
              {phases.map((phase) => (
                <div key={phase._id} className="mb-4">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={!!selected.phases[phase._id]}
                      onChange={() => togglePhase(phase._id)}
                      className="mr-2"
                    />
                    <span className="font-bold">{phase.title}</span>
                  </div>

                  <div className="pl-6">
                    {exercises
                      .filter((ex) => ex.phase_id === phase._id)
                      .map((ex) => (
                        <div key={ex._id} className="flex items-center mb-1">
                          <input
                            type="checkbox"
                            checked={!!selected.exercises[ex._id]}
                            onChange={() => toggleExercise(ex._id, phase._id)}
                            className="mr-2"
                          />
                          <span>{ex.title}</span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              <button
                onClick={handleSave}
                className="px-4 py-2 mt-4 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save
              </button>

              {/*savedData && (
                <div className="mt-4 p-4 bg-gray-100 rounded">
                  <h2 className="text-lg font-bold mb-2">Saved Data:</h2>
                  <pre className="bg-gray-200 p-2 rounded">
                    {JSON.stringify(savedData, null, 2)}
                  </pre>
                </div>
              )*/}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
